import { createHash, randomBytes } from "node:crypto";
import { openBrowser } from "../lib/open-browser";
import { responseError, whopApiFetch, whopFetch } from "../lib/http";
import { oauthBaseUrl, apiBaseUrl } from "./client";
import { startCallbackServer } from "./callback-server";
import { deriveProfileName, listProfiles, upsertProfile } from "./profiles";
import {
	type AccountChoice,
	printInfo,
	selectAccount,
	startSpinner,
} from "./prompts";
import type { Identity } from "./types";

/**
 * The registered public OAuth client for the Whop CLI. The same id is created on
 * local backends by the `cli_oauth_app:setup` rake task (backend/lib/tasks/
 * cli_oauth_app.rake, run by `rake dev:setup` on backend startup) so `whop login`
 * works identically against localhost and production — keep the two in sync.
 */
export const CLI_CLIENT_ID = "app_IoPWUwtO8c2gGA";

/** Fixed local callback port — must match the redirect_uri registered on the OAuth app. */
export const CLI_REDIRECT_URI = "http://localhost:13337/callback";

import { WHOP_SCOPES, type WhopScope } from "./scopes";

export { WHOP_SCOPES, type WhopScope };

/** Scopes requested by the CLI during OAuth login. */
export const CLI_SCOPES: WhopScope[] = [...WHOP_SCOPES];

// --- PKCE ---

export function generatePKCE(): { verifier: string; challenge: string } {
	const verifier = randomBytes(32).toString("base64url");
	const challenge = createHash("sha256").update(verifier).digest("base64url");
	return { verifier, challenge };
}

export function generateState(): string {
	return randomBytes(16).toString("base64url");
}

export function generateNonce(): string {
	return randomBytes(16).toString("base64url");
}

// --- Token exchange ---

export interface OAuthTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
}

async function requestToken(
	params: Record<string, string>,
): Promise<OAuthTokens> {
	const res = await whopFetch(`${oauthBaseUrl()}/oauth/token`, {
		method: "POST",
		headers: { "Content-Type": "application/x-www-form-urlencoded" },
		body: new URLSearchParams(params).toString(),
	});

	if (!res.ok) {
		const body = await res.text();
		throw new Error(`OAuth token request failed (${res.status}): ${body}`);
	}

	const json = (await res.json()) as {
		access_token: string;
		refresh_token: string;
		expires_in: number;
	};

	return {
		accessToken: json.access_token,
		refreshToken: json.refresh_token,
		expiresIn: json.expires_in,
	};
}

export function exchangeCode(
	code: string,
	verifier: string,
): Promise<OAuthTokens> {
	return requestToken({
		grant_type: "authorization_code",
		client_id: CLI_CLIENT_ID,
		code,
		redirect_uri: CLI_REDIRECT_URI,
		code_verifier: verifier,
	});
}

export function refreshAccessToken(refreshToken: string): Promise<OAuthTokens> {
	return requestToken({
		grant_type: "refresh_token",
		client_id: CLI_CLIENT_ID,
		refresh_token: refreshToken,
	});
}

// --- Authorization URL ---

export function buildAuthUrl(
	challenge: string,
	state: string,
	nonce: string,
): string {
	const base = oauthBaseUrl();
	const params = new URLSearchParams({
		client_id: CLI_CLIENT_ID,
		response_type: "code",
		redirect_uri: CLI_REDIRECT_URI,
		code_challenge: challenge,
		code_challenge_method: "S256",
		scope: CLI_SCOPES.join(" "),
		state,
		nonce,
	});
	return `${base}/oauth/authorize?${params.toString()}`;
}

// --- Full OAuth login flow ---

export class OAuthError extends Error {
	constructor(
		public readonly code: string,
		message: string,
	) {
		super(message);
		this.name = "OAuthError";
	}
}

export interface OAuthLoginResult {
	profileName: string;
	identity: Identity;
	existed: boolean;
}

export interface PreparedOAuthLogin {
	authorizationUrl: string;
	complete: () => Promise<OAuthLoginResult>;
}

/**
 * Every side effect of the OAuth login flow, injectable so tests can run the
 * full flow hermetically. `listOAuthAccounts` is the account-resolution
 * network boundary — keep it here so no test can escape to the real API.
 */
export interface OAuthLoginDependencies {
	startCallbackServer: typeof startCallbackServer;
	exchangeCode: typeof exchangeCode;
	fetch: typeof fetch;
	listOAuthAccounts: typeof listOAuthAccounts;
	listProfiles: typeof listProfiles;
	upsertProfile: typeof upsertProfile;
	deriveProfileName: typeof deriveProfileName;
	openBrowser: typeof openBrowser;
	startSpinner: typeof startSpinner;
	printInfo: typeof printInfo;
}

const defaultOAuthLoginDependencies: OAuthLoginDependencies = {
	startCallbackServer,
	exchangeCode,
	fetch: whopFetch,
	listOAuthAccounts,
	listProfiles,
	upsertProfile,
	deriveProfileName,
	openBrowser,
	startSpinner,
	printInfo,
};

/**
 * Resolves which business account an OAuth user acts on behalf of.
 *
 * OAuth tokens are user-scoped (`user_xxx`), so list the business accounts the
 * user can manage (`GET /accounts`) and pick one:
 *   - 0 business accounts → null (the user hasn't created one yet)
 *   - 1 business account  → use it
 *   - >1 → use --account-id if given, else prompt (TTY) / finish accountless
 *     (agent — pick later with `auth account`)
 */
/**
 * Lists the business accounts an OAuth user can manage (`GET /accounts`),
 * auto-paginating so a user with more than one page can still be resolved.
 * When `stopAtId` is given, stops as soon as it's found; otherwise the scan is
 * capped so pickers can't grow unbounded.
 */
export async function listOAuthAccounts(
	accessToken: string,
	opts: { stopAtId?: string | undefined } = {},
): Promise<AccountChoice[]> {
	const MAX_ACCOUNTS = 1000;
	const accounts: AccountChoice[] = [];
	let after: string | null = null;
	while (accounts.length < MAX_ACCOUNTS) {
		const url = new URL(`${apiBaseUrl().replace(/\/$/, "")}/accounts`);
		url.searchParams.set("first", "50");
		if (after) url.searchParams.set("after", after);

		const response = await whopApiFetch(url, accessToken);
		if (!response.ok) throw await responseError(response);
		const page = (await response.json()) as {
			data: AccountChoice[];
			page_info: { end_cursor: string | null; has_next_page: boolean };
		};
		for (const account of page.data) {
			accounts.push(account);
			if (opts.stopAtId && account.id === opts.stopAtId) return accounts;
			if (accounts.length >= MAX_ACCOUNTS) return accounts;
		}
		if (!page.page_info.has_next_page || !page.page_info.end_cursor) break;
		after = page.page_info.end_cursor;
	}
	return accounts;
}

async function resolveOAuthAccount(
	accessToken: string,
	opts: { agent: boolean; accountId?: string | undefined },
	dependencies: OAuthLoginDependencies,
): Promise<Identity | null> {
	// The spinner covers only the network fetch — it must stop before any prompt,
	// otherwise it renders on top of (and underneath) the business account picker.
	const s = opts.agent
		? null
		: dependencies.startSpinner("Loading your business accounts…");
	let accounts: AccountChoice[];
	try {
		accounts = await dependencies.listOAuthAccounts(accessToken, {
			stopAtId: opts.accountId,
		});
	} catch {
		s?.stop("Couldn't load your business accounts.");
		// An explicit --account-id can't be silently dropped: if the list failed to
		// load we can't verify it, so surface the error instead of logging in
		// without a business account. Without an explicit choice, stay non-fatal — API
		// commands will give a clear error rather than the login crashing.
		if (opts.accountId) {
			throw new OAuthError(
				"ACCOUNT_LOOKUP_FAILED",
				`Couldn't load your business accounts to verify "${opts.accountId}". Please try again.`,
			);
		}
		return null;
	}

	// An explicit --account-id is a hard request: verify it before the no-account
	// early return below, so an empty list errors (ACCOUNT_NOT_FOUND) rather than
	// silently logging the user in without the business account they asked for.
	if (opts.accountId) {
		const match = accounts.find((account) => account.id === opts.accountId);
		if (!match) {
			s?.stop("");
			const available = accounts.length
				? accounts.map((account) => account.id).join(", ")
				: "none — this user has no business accounts yet";
			throw new OAuthError(
				"ACCOUNT_NOT_FOUND",
				`No business account "${opts.accountId}" for this user. Available: ${available}.`,
			);
		}
		s?.stop(`Using ${match.title}.`);
		return match;
	}

	if (accounts.length === 0) {
		s?.stop("No business account found on your account.");
		return null;
	}

	if (accounts.length === 1) {
		s?.stop(`Using ${accounts[0].title}.`);
		return accounts[0];
	}

	// Agents can't use the interactive picker, but the tokens are already
	// exchanged — failing here would throw away a completed browser round-trip.
	// Finish the login without an account; the success CTA points at
	// `auth account` to pick one with the stored credentials.
	if (opts.agent) return null;

	// Stop the spinner before prompting so the picker is the only live UI.
	s?.stop(`Found ${accounts.length} business accounts.`);
	return selectAccount(accounts);
}

export async function prepareOAuthLogin(
	input: {
		profile?: string | undefined;
		interactive: boolean;
		accountId?: string | undefined;
		deferAccountSelection?: boolean | undefined;
	},
	dependencies: OAuthLoginDependencies = defaultOAuthLoginDependencies,
): Promise<PreparedOAuthLogin> {
	const { verifier, challenge } = generatePKCE();
	const state = generateState();
	const nonce = generateNonce();
	const authorizationUrl = buildAuthUrl(challenge, state, nonce);

	let callback: Awaited<ReturnType<typeof startCallbackServer>>;
	try {
		callback = await dependencies.startCallbackServer(state);
	} catch (error) {
		throw new OAuthError(
			"OAUTH_CALLBACK_FAILED",
			error instanceof Error ? error.message : "OAuth callback failed.",
		);
	}

	return {
		authorizationUrl,
		complete: async () => {
			let code: string;
			try {
				code = await callback.result;
			} catch (error) {
				throw new OAuthError(
					"OAUTH_CALLBACK_FAILED",
					error instanceof Error ? error.message : "OAuth callback failed.",
				);
			}

			const spinner = input.interactive
				? dependencies.startSpinner("Exchanging authorization code…")
				: null;
			let tokens: OAuthTokens;
			try {
				tokens = await dependencies.exchangeCode(code, verifier);
				spinner?.stop("Authorized.");
			} catch (error) {
				spinner?.stop("Token exchange failed.");
				throw new OAuthError(
					"TOKEN_EXCHANGE_FAILED",
					error instanceof Error ? error.message : "Token exchange failed.",
				);
			}

			const base = oauthBaseUrl();
			let identity: Identity = {};
			let accountIdentity: Identity | null = null;
			try {
				const response = await dependencies.fetch(`${base}/oauth/userinfo`, {
					headers: { Authorization: `Bearer ${tokens.accessToken}` },
				});
				if (response.ok) {
					const info = (await response.json()) as {
						sub?: string;
						name?: string;
						email?: string;
					};
					identity = { id: info.sub, title: info.name, email: info.email };
				}
			} catch {
				// User info is optional; profile naming falls back to the default.
			}

			if (!input.deferAccountSelection) {
				accountIdentity = await resolveOAuthAccount(
					tokens.accessToken,
					{ agent: !input.interactive, accountId: input.accountId },
					dependencies,
				);
			}

			const name = input.profile ?? dependencies.deriveProfileName(identity);
			const existed = dependencies
				.listProfiles()
				.some((profile) => profile.name === name);
			const tokenExpiresAt = new Date(
				Date.now() + tokens.expiresIn * 1000,
			).toISOString();

			const profile = dependencies.upsertProfile({
				name,
				method: "oauth",
				secret: tokens.refreshToken,
				identity,
				accountIdentity: accountIdentity ?? undefined,
				accessToken: tokens.accessToken,
				tokenExpiresAt,
			});

			return {
				profileName: profile.name,
				identity,
				existed,
			};
		},
	};
}

/** The interactive browser flow. Agents stream `prepareOAuthLogin` instead. */
export async function performOAuthLogin(
	input: {
		profile?: string | undefined;
		accountId?: string | undefined;
		deferAccountSelection?: boolean | undefined;
	},
	dependencies: OAuthLoginDependencies = defaultOAuthLoginDependencies,
): Promise<OAuthLoginResult> {
	const login = await prepareOAuthLogin(
		{ ...input, interactive: true },
		dependencies,
	);

	const spinner = dependencies.startSpinner("Opening browser…");
	dependencies.openBrowser(login.authorizationUrl);
	spinner.stop("Waiting for authorization in your browser…");
	dependencies.printInfo(
		`If your browser didn't open, visit:\n${login.authorizationUrl}`,
	);

	return login.complete();
}
